import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import { CalenderIcon, DropDownIcon } from "components/atoms/Icon";
import { Button, Typography } from "components/atoms";
import { MonthNavigator } from "../MonthNavigator";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";
import "./index.scss";

interface IProps {
  placeholder: string;
  setDateSelection: any;
  defaultSelected: any;
  inputFormat: any;
  isStudyTimer?: boolean;
  styles?: string;
  handleReschedule?: any;
  resetOnCancel?: () => void;
}

const daysofTheWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const DatePicker = ({
  placeholder,
  styles,
  setDateSelection,
  inputFormat,
  defaultSelected,
  isStudyTimer,
  handleReschedule,
  resetOnCancel
}: IProps) => {
  const [isShowItems, setIsShowItems] = useState<boolean>(false);

  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const [mobileDay, setMobileDay] = useState<any>();

  const calendarViewRef = useRef<HTMLDivElement>(null);

  const handleShowList = (event: any) => {
    event.preventDefault();
    setIsShowItems(!isShowItems);
  };

  const handleCancel = () => {
    setIsShowItems((prev) => false);
    if(resetOnCancel) {
      resetOnCancel();
    }
  };

  const handleApply = () => {
    setDateSelection(mobileDay);
    setIsShowItems(false);
    if (handleReschedule) {
      handleReschedule(mobileDay);
    }
  };

  const handleItemClick = () => {
    setIsShowItems(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      calendarViewRef.current &&
      !calendarViewRef.current.contains(event.target as Node)
    ) {
      setIsShowItems(false);
    }
  };

  const incrementMonth = () => {
    setCurrentMonth((prevMonth) => prevMonth.add(1, "month"));
  };

  const decrementMonth = () => {
    setCurrentMonth((prevMonth) => prevMonth.subtract(1, "month"));
  };

  const handleMobileDayClick = (clickDay: any) => {
    setMobileDay(clickDay);
    setDateSelection(clickDay);
  };

  const mobileRenderBody = () => {
    const monthStart = currentMonth.startOf("month");
    const monthEnd = currentMonth.endOf("month");
    const startDate = monthStart.startOf("week");
    const endDate = monthEnd.endOf("week");

    const weeks = [];
    let week = [];

    for (let day = startDate; day.isBefore(endDate); day = day.add(1, "day")) {
      if (day.isSameOrAfter(monthStart) && day.isSameOrBefore(monthEnd)) {
        week.push({
          dateNumber: day,
          inCurrentMonth: true,
        });
      } else {
        week.push({
          dateNumber: day,
          inCurrentMonth: false,
        });
      }
      if (day.day() === 6) {
        weeks.push(week);
        week = [];
      }
    }

    return weeks.map((week, i) => (
      <tr key={i}>
        {week.map((day, j) => (
          <td key={j}>
            <div
              className={classNames({
                "tbody-td-parent-wrapper": true,
                "blur-date-number": day.inCurrentMonth === false,
              })}
            >
              <div
                className="header-date-number-wrapper"
                onClick={() => handleMobileDayClick(day.dateNumber)}
              >
                <Typography
                  variant="h8Med"
                  label={day.dateNumber.format("D")}
                  styles={
                    day.inCurrentMonth === true &&
                    day.dateNumber.format("DD/MM/YYYY") ===
                      dayjs().format("DD/MM/YYYY")
                      ? "today-date-typography-custom-style day-number-custom-style"
                      : "date-typography-custom-style day-number-custom-style"
                  }
                />
              </div>
            </div>
          </td>
        ))}
      </tr>
    ));
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={calendarViewRef}
      className={classNames("date-picker-wrapper", "date-picker", {
        [`${styles}`]: styles,
      })}
    >
      <div
        className={classNames("selected-item-wrapper")}
        onClick={handleShowList}
      >
        <div className={classNames("selected-item")}>
          <p>
            {defaultSelected
              // ? dayjs(defaultSelected).format(inputFormat)
              ? dayjs(defaultSelected).format(inputFormat)
              : placeholder}
          </p>
        </div>

        <div
          className={classNames(
            "date-picker-icon",
            isShowItems && "date-picker-icon-hide"
          )}
        >
          <CalenderIcon color="white" />
        </div>
      </div>

      {isShowItems && (
        <div className={classNames("date-picker-item-wrapper")}>
          <div className="date-picker-nav-wrapper">
            {isStudyTimer ? (
              <div className="today-label-section">
                <Typography
                  variant="h8Med"
                  label="Today"
                  styles="today-typography-custom-style"
                  onClick={() => {
                    setCurrentMonth(dayjs());
                  }}
                />
              </div>
            ) : (
              <Button
                icon={false}
                label="Today"
                type="score"
                onClick={() => {
                  setCurrentMonth(dayjs());
                }}
              />
            )}

            <MonthNavigator
              incrementMonth={incrementMonth}
              decrementMonth={decrementMonth}
              text={currentMonth.format("MMMM YYYY")}
            />
          </div>
          <div className="dates-wrapper">
            <table className="study-table-mobile" rules="none">
              <thead>
                <tr>
                  {daysofTheWeek.map((weekday, index) => (
                    <th key={index}>
                      <Typography
                        variant="h8Med"
                        label={weekday[0]}
                        styles={
                          weekday[0] === "S"
                            ? "day-name-custom-style-gray"
                            : "day-name-custom-style-black"
                        }
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{mobileRenderBody()}</tbody>
            </table>
          </div>
          <div
            className={classNames(
              isStudyTimer ? "study-btn-wrapper" : "btn-wrapper"
            )}
          >
            {isStudyTimer ? (
              <Button
                icon={false}
                label="Cancel"
                type="online"
                onClick={() => handleCancel()}
              />
            ) : null}
            <Button
              icon={false}
              label="Apply"
              type="score"
              onClick={() => {
                // setDateSelection(mobileDay);
                // setIsShowItems(false);
                handleApply();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
